import { useCallback } from "react";
import { Box, Center, Group, Loader, Paper, SimpleGrid, Stack, Text } from "@mantine/core";
import { IconBuildingFactory2 } from "@tabler/icons-react";
import { useLocation, useNavigate } from "react-router-dom";
import { type CompanyWithUI } from "@/components/shared/CompanyCardHorizontal";
import DiscoverCompanyCard from "@/pages/discover/components/DiscoverCompanyCard";
import { useUser } from "@/context/UserContext";
import { navigateToCompanyWorkspace } from "@/shared/utils/collaborationNavigation.util";
import { successNotification } from "@/shared/utils/successNotification";
import { handleTrpcError } from "@/shared/utils/trpc/handletrpcError.util";
import type { SectorEcosystemOverview } from "../../../../../../shared/sectorEcosystemOverview";
import type { SectorListFooterLink } from "../sectorCompaniesList.const";
import { useSectorCompaniesPreview } from "../hooks/useSectorCompaniesPreview";
import SectorDashboardCard from "./SectorDashboardCard";

type CompaniesOverviewProps = {
  slug: string;
  overview: SectorEcosystemOverview;
  isAuthenticated: boolean;
} & SectorListFooterLink;

function FeaturedCompaniesComingSoonContent() {
  return (
    <Paper
      p="md"
      radius="md"
      style={{
        border: "1px dashed #CBD5E1",
        backgroundColor: "#F8FAFC",
      }}
    >
      <Stack gap="sm" align="center" py="sm">
        <Center
          style={{
            width: 40,
            height: 40,
            borderRadius: "50%",
            backgroundColor: "#EFF6FF",
          }}
        >
          <IconBuildingFactory2 size={20} color="#2563EB" />
        </Center>
        <Text
          size="xs"
          fw={600}
          c="#2563EB"
          px={10}
          py={4}
          style={{ borderRadius: 999, backgroundColor: "#EFF6FF" }}
        >
          Launching Soon
        </Text>
        <Stack gap={4} align="center" maw={300}>
          <Text size="sm" fw={600} c="#334155" ta="center">
            Companies are in the onboarding queue
          </Text>
          <Text size="xs" c="dimmed" ta="center">
            Featured companies for this sector are being onboarded and will appear here once live.
          </Text>
        </Stack>
      </Stack>
    </Paper>
  );
}

export default function CompaniesOverview({
  slug,
  footerTo,
  footerLinkState,
}: CompaniesOverviewProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const sectorReturnTo = `${location.pathname}${location.search}${location.hash}`;
  const { companies, setCompanies, loading, error } = useSectorCompaniesPreview(slug);
  const { addToShortlist, removeFromShortlist } = useUser();

  const companiesListReady = !loading && companies.length > 0;

  const handleShortlistClick = useCallback(
    async (id: string, isShortlisted: boolean) => {
      let previous = false;
      setCompanies((prev) =>
        prev.map((c) => {
          if (c.id !== id) return c;
          previous = Boolean(c.isShortlisted);
          return { ...c, isShortlisted };
        }),
      );
      try {
        if (isShortlisted) {
          await addToShortlist(id);
          successNotification({ title: "Success", message: "Company added to shortlist" });
        } else {
          await removeFromShortlist(id);
          successNotification({ title: "Success", message: "Company removed from shortlist" });
        }
      } catch (err) {
        handleTrpcError(err);
        setCompanies((prev) => prev.map((c) => (c.id === id ? { ...c, isShortlisted: previous } : c)));
      }
    },
    [setCompanies, addToShortlist, removeFromShortlist],
  );

  const handleViewDetails = useCallback(
    (companyId: string) => {
      void navigateToCompanyWorkspace(navigate, companyId, {
        state: { discoverReturnTo: sectorReturnTo },
      });
    },
    [navigate, sectorReturnTo],
  );

  return (
    <Box style={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}>
    <SectorDashboardCard
      title="Featured Companies"
      footerLabel="View All Companies"
      footerComingSoon={!companiesListReady}
      footerTo={companiesListReady ? footerTo : undefined}
      footerLinkState={companiesListReady ? footerLinkState : undefined}
    >
      {loading ? (
        <Group justify="center" py="lg">
          <Loader size="sm" />
        </Group>
      ) : error ? (
        <Text size="sm" c="dimmed" ta="center" py="md">
          {error}
        </Text>
      ) : companies.length === 0 ? (
        <FeaturedCompaniesComingSoonContent />
      ) : (
        <SimpleGrid
          type="container"
          cols={{ base: 1, "360px": 2, "560px": 3 }}
          spacing="sm"
          verticalSpacing="sm"
        >
          {companies.map((company: CompanyWithUI) => (
            <DiscoverCompanyCard
              key={company.id}
              company={company}
              compact
              hideTitle
              hideIndustry
              discoverReturnTo={sectorReturnTo}
              secondaryColumn="industry"
              onViewDetails={handleViewDetails}
              onShortlistClick={handleShortlistClick}
            />
          ))}
        </SimpleGrid>
      )}
    </SectorDashboardCard>
    </Box>
  );
}
